import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DefaultSerializer } from 'v8';

@Component({
  selector: 'vb-size',
  templateUrl: './size.component.html',
  styleUrls: ['./size.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SizeComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false  //NOSONAR
})
export class SizeComponent implements OnInit, OnDestroy {
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);

  @Input() element: any;
  @Input() disableField = false;
  private modelValue: any;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  disabled: boolean;

  units: any[] = [
    { name: 'px', code: 'px' },
    { name: '%', code: '%' },
    { name: 'em', code: 'em' },
    { name: 'rem', code: 'rem' }
  ];

  size: any = 12;
  unit = 'px';
  maxFontSize: any;
  minFontSize: any;
  defaultSize: any;

  @Input() currentPage: any;
  @Input() isTemplateFontGroup: any;
  @Input() currNodeType: any = null;

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.setValue(value);
    this.updateSizeConstraints();
    this.modelValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    this.disabled = isDisabled;
  }

  /**
  The updateSizeConstraints method sets the maximum, default, and minimum font sizes
  based on the specified unit ('px', 'em', 'rem', or '%').
  */
  updateSizeConstraints() {
    switch (this.unit) {
      case 'px':
        this.maxFontSize = 32;
        this.defaultSize = 12;
        this.minFontSize = 10;
        break;
      case 'em':
      case 'rem':
        this.maxFontSize = 2;
        this.defaultSize = 1;
        this.minFontSize = 0;
        break;
      case '%':
        this.maxFontSize = 200;
        this.defaultSize = 75;
        this.minFontSize = 60;
        break;
    }
  }

  /**
   The handleSizeChange method validates and updates the font size when the input value changes,
   ensuring it is within a specified range for the "appTitle", and displays an error message if it is not. 
  */
  handleSizeChange() {
    if ((this.currNodeType === 'appTitle' || this.currNodeType === 'APP_NAME') && (this.size < this.minFontSize || this.size > this.maxFontSize)) {
      this.showMessage(
        'error',
        this.translate.instant('MAX_FONT_SIZE', {
          fontType: this.unit,
          minFontSize: this.minFontSize,
          maxFontSize: this.maxFontSize
        })
      );
    } else if (this.size != null && this.unit) {
      this.set(this.size + this.unit);
    }
  }

  /**
   The handleUnitChange method updates size constraints and resets the size to the default value
  */
  handleUnitChange() {
    if (this.currNodeType === 'appTitle' || this.currNodeType === 'APP_NAME') {
      this.updateSizeConstraints();
      this.size = this.defaultSize;
    }
    this.set(this.size + this.unit);
  }

  setValue(value: any) {
    if (value && this.currentPage?.currentPage !== 'templates') {
      this.size = parseInt(value.replace(/[^0-9]/g, ''));
      this.unit = value.replace(/[0-9]/g, '');
    } else if (this.currentPage?.currentPage === 'templates') {
      this.size = parseFloat(value.replace(/[^0-9]\./g, ''));
      this.unit = value.replace(/[0-9]/g, '')?.replace(/\./g, '');
    }
  }

  ngOnInit(): void {
    this.updateSizeConstraints();
  }

  ngOnDestroy(): void {
    if ($(this.elementRef.nativeElement)) {
      $(this.elementRef.nativeElement).remove();
    }
  }

  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }
}
